import { ListItemIcon, ListItemText } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { StyledListItem } from '@/components/AssetsPage/PopoverList';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { CopiableColumn } from '@/types/copiableColumn';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

const MAX_DISPLAY_LENGTH = 50;

const truncateValue = (value: string): string => {
  if (value.length <= MAX_DISPLAY_LENGTH) return value;
  return `${value.slice(0, MAX_DISPLAY_LENGTH)}...`;
};

type ColumnWithOptionalCopy<T extends GridValidRowModel> = GridColDef<T> & Partial<CopiableColumn<T>>;

export const getCopyValueFromColumn = <T extends GridValidRowModel>(
  columns: ColumnWithOptionalCopy<T>[],
  row: T | undefined,
  field: string | undefined
): string | undefined => {
  if (!row || !field) return undefined;
  const column = columns.find(col => col.field === field);
  if (!column?.getCopyValue) return undefined;
  return column.getCopyValue(row);
};

export const CopyMenuItem = <T extends GridValidRowModel>({
  columns,
  row,
  field,
  onCopy,
}: {
  columns: ColumnWithOptionalCopy<T>[];
  row: T | undefined;
  field: string | undefined;
  onCopy?: () => void;
}) => {
  const { copy } = useCopyToClipboard();

  if (!row || !field) return;

  const column = columns.find(col => col.field === field);
  if (!column?.getCopyValue) return;

  const copyValue = column.getCopyValue(row);
  if (!copyValue) return;

  const handleCopy = () => {
    copy(copyValue, truncateValue(copyValue));
    onCopy?.();
  };

  return (
    <StyledListItem onClick={handleCopy}>
      <ListItemIcon>
        <ContentCopyIcon sx={{ fontSize: '18px' }} />
      </ListItemIcon>
      <ListItemText>{`Copy '${truncateValue(copyValue)}'`}</ListItemText>
    </StyledListItem>
  );
};
